// Base-closure matrix modal (Step 5e Phase 7b) — built to the signed-off mockup
// `admin-ui-mockup-base-closure-and-orphans.html`: one row per homed resource, columns = each
// other active base (reallocate) + a Deactivate column; aircraft→no-ICAO target = amber warning
// (allowed), asset→name collision at target = blocked; base reason required; footer tally.
// Submit posts the resolution payload to DELETE /api/bases/:id (baseCloseSchema).

import { useEffect, useState, type CSSProperties } from 'react';
import { C, F, Modal, Btn, ErrorText } from './adminUi';
import { StepUpFields } from './StepUp';
import {
  closurePreview, closeBase, BASE_REASONS, AIRCRAFT_ASSET_REASONS, STAFF_REASONS,
  type ClosurePreview, type ClosureResolution, type ClosureSummary,
} from '../../lib/admin';

type Choice = { choice: string /* baseId | 'deact' */; reason?: string };

const BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  aircraft: { bg: C.accentLight, fg: C.accent, label: 'Aircraft' },
  asset: { bg: C.purpleLight, fg: C.purple, label: 'Asset' },
  user: { bg: '#e0e7ff', fg: '#4338ca', label: 'Staff' },
};
const th: CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: C.textMuted, fontWeight: 700, padding: '9px 10px', textAlign: 'center', borderBottom: `1px solid ${C.border}` };
const td: CSSProperties = { padding: '9px 10px', textAlign: 'center', borderBottom: `1px solid ${C.border}` };

export default function BaseClosureModal({ baseId, onClose, onClosed }: { baseId: string; onClose: () => void; onClosed: (summary: ClosureSummary) => void }) {
  const [preview, setPreview] = useState<ClosurePreview | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [baseReason, setBaseReason] = useState('');
  const [baseNote, setBaseNote] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    closurePreview(baseId).then((r) => { if (!live) return; if (r.error || !r.data) setLoadErr(r.error ?? 'Failed to load.'); else setPreview(r.data); });
    return () => { live = false; };
  }, [baseId]);

  function pick(resId: string, choice: string) {
    setChoices((c) => ({ ...c, [resId]: { choice, reason: choice === 'deact' ? (c[resId]?.reason ?? '') : undefined } }));
  }
  function setReason(resId: string, reason: string) {
    setChoices((c) => ({ ...c, [resId]: { ...c[resId], reason } }));
  }

  const resources = preview?.resources ?? [];
  const targets = preview?.other_active_bases ?? [];
  let resolved = 0; let ok = !!baseReason;
  for (const r of resources) {
    const ch = choices[r.id];
    if (!ch) { ok = false; continue; }
    if (ch.choice === 'deact' && !ch.reason) { ok = false; continue; }
    resolved++;
  }
  const deactN = resources.filter((r) => choices[r.id]?.choice === 'deact').length;
  const moveN = resources.length - deactN;
  // Closure is destructive → step-up password is mandatory.
  const canSubmit = ok && password.length > 0;

  async function commit() {
    if (!preview) return;
    setSubmitting(true); setSubmitErr(null);
    const resolutions: ClosureResolution[] = resources.map((r) => {
      const ch = choices[r.id]!;
      return ch.choice === 'deact'
        ? { kind: r.kind, id: r.id, action: 'deactivate', reason: ch.reason }
        : { kind: r.kind, id: r.id, action: 'reallocate', target_base_id: ch.choice };
    });
    const res = await closeBase(baseId, { reason: baseReason, ...(baseNote ? { note: baseNote } : {}), resolutions, step_up: { password } });
    setSubmitting(false);
    if (res.error || !res.data) { setSubmitErr(res.error ?? 'Closure failed.'); return; }
    onClosed(res.data.summary);
  }

  const reasonOpts = (kind: string) => (kind === 'user' ? STAFF_REASONS : AIRCRAFT_ASSET_REASONS);

  return (
    <Modal title="Close base" onClose={onClose} width={760}>
      {loadErr && <ErrorText>{loadErr}</ErrorText>}
      {!preview && !loadErr && <div style={{ color: C.textMuted, padding: 20 }}>Loading homed resources…</div>}
      {preview && (
        <>
          <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>Close {preview.base.name} ({preview.base.short_code})</h2>
          <p style={{ color: C.textDim, fontSize: 13, margin: '0 0 12px' }}>
            Deactivating a base is permanent and audited. Every resource homed here must be reallocated or deactivated before the closure can commit — one transaction, nothing changes until you confirm.
          </p>
          <div style={{ display: 'flex', gap: 10, background: C.amberLight, border: `1px solid ${C.amberBorder}`, color: '#7a4a08', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
            <span>⚠️</span>
            <div><b>{preview.bookings_to_orphan} future booking(s)</b> at this base (and on resources homed here) will be moved to <b>Orphaned bookings</b> for review. Past bookings are kept untouched.</div>
          </div>

          {resources.length === 0 ? (
            <div style={{ color: C.textDim, fontSize: 13, padding: '8px 0 14px' }}>No resources are homed at this base — it can be closed directly.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Resource</th>
                  <th style={th} colSpan={Math.max(targets.length, 1)}><span style={{ color: C.accent, fontSize: 10, fontWeight: 700 }}>REALLOCATE TO</span></th>
                  <th style={{ ...th, borderLeft: `1px solid ${C.borderMed}`, background: C.surfaceAlt }}>Deactivate</th>
                </tr>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }} />
                  {targets.length === 0 ? <th style={th}><span style={{ fontSize: 10, color: C.textMuted }}>no other active base</span></th>
                    : targets.map((t) => <th key={t.id} style={th}>{t.name} ({t.short_code}){t.icao ? '' : ' ⚠'}</th>)}
                  <th style={{ ...th, borderLeft: `1px solid ${C.borderMed}`, background: C.surfaceAlt }} />
                </tr>
              </thead>
              <tbody>
                {resources.map((r) => {
                  const ch = choices[r.id];
                  const b = BADGE[r.kind];
                  return (
                    <tr key={r.id}>
                      <td style={{ ...td, textAlign: 'left', whiteSpace: 'nowrap' }}>
                        {r.label}
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, marginLeft: 7, background: b.bg, color: b.fg, textTransform: 'uppercase' }}>{b.label}</span>
                      </td>
                      {targets.length === 0 ? <td style={td}><span style={{ fontSize: 10, color: C.textMuted }}>—</span></td>
                        : targets.map((t) => {
                          const collides = r.kind === 'asset' && (r.collision_base_ids ?? []).includes(t.id);
                          const noIcaoWarn = r.kind === 'aircraft' && !t.icao;
                          return (
                            <td key={t.id} style={td}>
                              <input type="radio" name={r.id} disabled={collides} checked={ch?.choice === t.id} onChange={() => pick(r.id, t.id)} style={{ width: 16, height: 16, accentColor: C.accent, cursor: collides ? 'not-allowed' : 'pointer' }} />
                              {collides && <span style={{ display: 'block', fontSize: 10, color: C.red, marginTop: 3 }}>⊘ name collides — rename first</span>}
                              {noIcaoWarn && <span style={{ display: 'block', fontSize: 10, color: C.amber, marginTop: 3 }}>⚠ no ICAO — allowed, unresolvable until set</span>}
                            </td>
                          );
                        })}
                      <td style={{ ...td, borderLeft: `1px solid ${C.borderMed}`, background: C.surfaceAlt }}>
                        <input type="radio" name={r.id} checked={ch?.choice === 'deact'} onChange={() => pick(r.id, 'deact')} style={{ width: 16, height: 16, accentColor: C.accent }} />
                        {ch?.choice === 'deact' && (
                          <select value={ch.reason ?? ''} onChange={(e) => setReason(r.id, e.target.value)} style={{ display: 'block', marginTop: 4, fontFamily: F, fontSize: 11, padding: '3px 5px', border: `1px solid ${C.borderMed}`, borderRadius: 5, width: '100%' }}>
                            <option value="">reason *</option>
                            {reasonOpts(r.kind).map((rs) => <option key={rs} value={rs}>{rs}</option>)}
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
            <div style={{ minWidth: 200 }}>
              <label style={{ fontWeight: 600, fontSize: 12, color: C.textDim, display: 'block', marginBottom: 3 }}>Reason for closing this base <span style={{ color: C.red }}>*</span></label>
              <select value={baseReason} onChange={(e) => setBaseReason(e.target.value)} style={{ fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, width: '100%' }}>
                <option value="">— select —</option>
                {BASE_REASONS.map((rs) => <option key={rs} value={rs}>{rs}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label style={{ fontWeight: 600, fontSize: 12, color: C.textDim, display: 'block', marginBottom: 3 }}>Note (optional)</label>
              <input type="text" value={baseNote} onChange={(e) => setBaseNote(e.target.value)} placeholder="e.g. Darwin operations consolidated into Archerfield" style={{ fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, width: '100%' }} />
            </div>
          </div>

          <StepUpFields password={password} setPassword={setPassword} />

          {submitErr && <ErrorText>{submitErr}</ErrorText>}

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
            <Btn kind="danger" disabled={!canSubmit || submitting} onClick={commit}>{submitting ? 'Closing…' : 'Close base & apply changes'}</Btn>
            <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
            <span style={{ fontSize: 13, color: C.textDim, marginLeft: 'auto' }}>
              <b style={{ color: C.text }}>{resolved}/{resources.length}</b> resolved · {moveN} reallocated · {deactN} deactivated · <b style={{ color: C.text }}>{preview.bookings_to_orphan}</b> → Orphaned
            </span>
          </div>
        </>
      )}
    </Modal>
  );
}
